/**
 * Opciones de orden que OFRECE el selector del listado, en el orden en que se
 * muestran. Es una sola lista para las cuatro superficies que la dibujan
 * (panel expandido y riel colapsado del home, drawer del celular y /vehicles):
 * si alguna mostrara otra cosa, un mismo link daría selectores distintos.
 *
 * Sólo se listan precio y año. El orden por defecto del listado es el INICIAL
 * (`'default'`, ticket #164): es lo que el store arranca, lo que la URL omite
 * (un link sin `orden=` significa eso) y el único que respeta lo que la
 * automotora configuró desde el admin (más nuevos o más antiguos primero).
 * Hasta ese ticket el default era `date_desc` fijo para todas, que ahora es una
 * elección explícita del visitante y por lo tanto SÍ viaja en el link.
 *
 * Sigue fuera del selector a propósito, así que con el orden por defecto el
 * botón muestra "Ordenar por" sin ninguna opción marcada. Los comparadores de
 * `date_*` y `mileage_*` siguen existiendo en los listados: un link viejo con
 * `orden=mileage_asc` ordena igual que antes, sólo que el selector no lo marca.
 */
import { ORDEN_INICIAL } from '@/lib/orden-vehiculos';

export const ORDEN_POR_DEFECTO: string = ORDEN_INICIAL;

export const OPCIONES_ORDEN = [
  { key: 'price_asc', i18n: 'vehicles.sorting.priceAsc', fallback: 'Precio: Menor a Mayor', icon: 'mdi:sort-ascending' },
  { key: 'price_desc', i18n: 'vehicles.sorting.priceDesc', fallback: 'Precio: Mayor a Menor', icon: 'mdi:sort-descending' },
  { key: 'year_desc', i18n: 'vehicles.sorting.yearDesc', fallback: 'Año: Más Reciente', icon: 'mdi:calendar' },
  { key: 'year_asc', i18n: 'vehicles.sorting.yearAsc', fallback: 'Año: Más Antiguo', icon: 'mdi:calendar-outline' },
] as const;

export type ClaveOrden = (typeof OPCIONES_ORDEN)[number]['key'];

/**
 * Los mismos órdenes, agrupados por criterio para el selector.
 *
 * QA pidió juntar los pares "Precio: Menor a Mayor / Mayor a Menor" (y los de
 * año y kilometraje), que ocupaban el doble de menú para decir una sola cosa.
 * Las CLAVES no cambian —siguen siendo `price_asc`, `year_desc`, …— así que
 * los comparadores de los listados, los links viejos con `?orden=` y el orden
 * por defecto (`ORDEN_POR_DEFECTO`, que sigue fuera del selector) funcionan
 * igual que antes: lo único que cambia es cómo se dibujan.
 */
export const CRITERIOS_ORDEN = [
  // `inicial`: con qué dirección arranca cada criterio la primera vez que se
  // toca. No es siempre ascendente: en precio y kilometraje lo útil es "de
  // menor a mayor", pero en año lo que se busca es el auto MÁS NUEVO, que es
  // descendente.
  { criterio: 'price', asc: 'price_asc', desc: 'price_desc', inicial: 'asc' },
  { criterio: 'year', asc: 'year_asc', desc: 'year_desc', inicial: 'desc' },
  { criterio: 'mileage', asc: 'mileage_asc', desc: 'mileage_desc', inicial: 'asc' },
] as const;

/**
 * La etiqueta de cada orden, que ya trae el criterio Y la dirección escritos
 * ("Precio: Menor a Mayor", "Año: Más Reciente"…). Son las claves que el repo
 * ya tenía en `i18n/locales/{es,en}/vehicles.ts`: no se inventan textos nuevos
 * ni se muestran claves crudas.
 */
export const I18N_POR_ORDEN: Record<string, { i18n: string; fallback: string }> = {
  price_asc: { i18n: 'vehicles.sorting.priceAsc', fallback: 'Precio: Menor a Mayor' },
  price_desc: { i18n: 'vehicles.sorting.priceDesc', fallback: 'Precio: Mayor a Menor' },
  year_desc: { i18n: 'vehicles.sorting.yearDesc', fallback: 'Año: Más Reciente' },
  year_asc: { i18n: 'vehicles.sorting.yearAsc', fallback: 'Año: Más Antiguo' },
  mileage_asc: { i18n: 'vehicles.sorting.mileageAsc', fallback: 'Kilometraje: Menor a Mayor' },
  mileage_desc: { i18n: 'vehicles.sorting.mileageDesc', fallback: 'Kilometraje: Mayor a Menor' },
  date_desc: { i18n: 'vehicles.sorting.dateDesc', fallback: 'Recientes primero' },
  date_asc: { i18n: 'vehicles.sorting.dateAsc', fallback: 'Antiguos primero' },
};

/**
 * Las tres filas del selector, con la dirección escrita al lado del criterio.
 *
 * Cada fila muestra **la dirección que va a aplicar al tocarla**: si el
 * criterio no está activo es su dirección inicial, y si ya lo está es la
 * contraria a la puesta. Así tocar "Precio: Menor a Mayor" ordena de menor a
 * mayor y la fila pasa a ofrecer "Precio: Mayor a Menor", que es el próximo
 * toque. Siguen siendo 3 filas, nunca 6.
 *
 * `activo`/`ascendente` marcan cuál está puesto (para el tilde y el ícono), y
 * las claves internas no cambian.
 */
export function opcionesOrdenAgrupadas(ordenActual: string | null | undefined) {
  return CRITERIOS_ORDEN.map((c) => {
    const ascendente = ordenActual === c.asc;
    const activo = ascendente || ordenActual === c.desc;
    // Activo: la contraria a la puesta. Inactivo: su dirección inicial.
    const proximo = activo
      ? ascendente
        ? c.desc
        : c.asc
      : c.inicial === 'desc'
        ? c.desc
        : c.asc;
    const etiqueta = I18N_POR_ORDEN[proximo];
    return {
      criterio: c.criterio,
      key: proximo,
      i18n: etiqueta.i18n,
      fallback: etiqueta.fallback,
      activo,
      ascendente,
      icon: activo
        ? ascendente
          ? 'mdi:sort-ascending'
          : 'mdi:sort-descending'
        : 'mdi:sort',
    };
  });
}

/**
 * Qué dice el botón cerrado: el criterio Y la dirección que están puestos
 * ("Kilometraje: Menor a Mayor"). `undefined` cuando el orden es el de por
 * defecto o uno que el selector no lista, para que el botón siga mostrando su
 * rótulo genérico como antes.
 */
export function etiquetaOrdenActivo(ordenActual: string | null | undefined) {
  if (!ordenActual || ordenActual === ORDEN_POR_DEFECTO) return undefined;
  const listado = CRITERIOS_ORDEN.some((c) => c.asc === ordenActual || c.desc === ordenActual);
  return listado ? I18N_POR_ORDEN[ordenActual] : undefined;
}

/**
 * Prioridad por disponibilidad de un vehículo: 0 disponible, 1 reservado,
 * 2 vendido. Cuanto más bajo, más arriba va en el listado.
 *
 * Es LA MISMA definición que la página /vehicles ya aplicaba antes de ordenar
 * —de ahí se mudó, tal cual— y por eso vive acá: las dos superficies que la
 * necesitan la leen del mismo lugar en vez de repetirla. Todo estado que no
 * sea Vendido ni Reservado cuenta como disponible, incluidos los
 * personalizados con `show_in_web`, igual que en `VehicleCard2` y el sitemap.
 *
 * `VehicleList` agrupa igual con cuatro `if` propios, escritos antes que esto
 * (jun-2025) y equivalentes par por par. No se tocaron: esa superficie ya
 * funciona y el ticket #129 no la necesita. Si algún día hay que editar ese
 * orden, el cambio va en los dos lados.
 */
export function puntajeDisponibilidad(v: {
  status?: { name?: string | null } | null;
}): number {
  const estado = v.status?.name;
  if (estado === 'Vendido') return 2;
  if (estado === 'Reservado') return 1;
  return 0;
}

/**
 * Comparador para poner los disponibles antes que los reservados y los
 * vendidos (ticket #129).
 *
 * Devuelve 0 cuando los dos vehículos caen en el mismo grupo, así que se usa
 * como criterio EXTERNO: el orden que eligió el visitante (precio, año,
 * kilometraje, fecha…) sigue decidiendo DENTRO de cada grupo. Eso se apoya en
 * que `Array.prototype.sort` es estable desde ES2019, así que un 0 conserva el
 * orden con el que la lista venía.
 *
 * No se usa solo: quien ordena lo aplica primero y, si da 0, cae a su propio
 * switch. Apagado, ninguna superficie lo llama y el orden es el de siempre.
 */
export function compararDisponibilidad(
  a: { status?: { name?: string | null } | null },
  b: { status?: { name?: string | null } | null }
): number {
  return puntajeDisponibilidad(a) - puntajeDisponibilidad(b);
}
